import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { PageHeader } from '../components/ui/PageHeader'
import { authService } from '../services'
import toast from 'react-hot-toast'
import { setDocumentTitle, usePageTitle } from '../utils/translation'

// LINE icon component
const LineIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='#00B900'
  >
    <path d='M19.11 17.205c-.372 0-.754-.019-1.108-.058-1.86-.207-3.716-.662-5.417-1.318-.847-.33-1.549-.779-2.228-1.357-.687-.584-1.22-1.183-1.654-1.88-.53-.846-.796-1.735-.796-2.618 0-2.532 2.001-4.585 4.46-4.585.743 0 1.463.153 2.138.454.682.291 1.27.766 1.72 1.389.459.636.786 1.363.963 2.156.176.784.169 1.598-.022 2.367-.226.92-.629 1.74-1.178 2.409-.656.808-1.499 1.39-2.464 1.691-.906.283-1.896.42-2.915.42h-2.4zm1.86-10.644c-.374-.222-.84-.334-1.384-.334-2.22 0-4.023 1.857-4.023 4.14 0 2.301 1.81 4.175 4.024 4.175 2.235 0 4.058-1.874 4.058-4.175 0-2.262-1.825-4.14-4.059-4.14-.39 0-.779.063-1.138.19'></path>
  </svg>
)

// Custom type for user
interface User {
  email?: string
  phone?: string
  email_verified?: boolean
  line_id?: string
  // Add other user properties as needed
}

export default function ConnectLine() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [loading, setLoading] = useState<boolean>(false)
  const [user, setUser] = useState<User | null>(null)
  const [lineConnected, setLineConnected] = useState<boolean>(false)

  // Get translated page title
  const pageTitle = usePageTitle('account.lineConnection.title', 'Connect with LINE')

  useEffect(() => {
    // Set the document title
    setDocumentTitle(pageTitle)

    // Check if user is authenticated
    if (!authService.isAuthenticated()) {
      navigate('/login')
      return
    }

    // Fetch user data to check if LINE is already connected
    fetchUserData()
  }, [navigate, pageTitle])

  const fetchUserData = async () => {
    try {
      const userData = (await authService.getCurrentUser()) as User
      setUser(userData)
      // Check if line_id exists in the user data
      setLineConnected(!!userData?.line_id)
    } catch (error) {
      console.error('Error fetching user data:', error)
      toast.error(t('common.errorOccurred'))
    }
  }

  const handleConnectLine = async () => {
    setLoading(true)
    try {
      // Use the authService method to handle LINE connection
      await authService.connectLine()

      // The page will navigate away to LINE login, but if it doesn't work:
      setTimeout(() => {
        setLoading(false)
        toast.error(t('account.lineConnection.redirectError'))
      }, 5000)
    } catch (error) {
      console.error('Error connecting with LINE:', error)
      toast.error(t('account.lineConnection.connectError'))
      setLoading(false)
    }
  }

  const handleDisconnectLine = async () => {
    setLoading(true)
    try {
      // Use the authService method to disconnect LINE
      const response = await authService.disconnectLine()

      if (response.success) {
        setLineConnected(false)
        toast.success(t('account.lineConnection.disconnectSuccess'))
      } else {
        toast.error(response.message || t('account.lineConnection.disconnectError'))
      }
    } catch (error) {
      console.error('Error disconnecting LINE:', error)
      toast.error(t('account.lineConnection.disconnectError'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <main className='min-h-screen bg-gray-50'>
      <PageHeader
        title={t('account.lineConnection.title')}
        showBackButton={true}
        onBack={() => navigate("/me")}
      />

      <div className='p-4 space-y-4'>
        <div className='bg-white rounded-xl p-4'>
          <div className='flex flex-col items-center justify-center py-6'>
            <LineIcon />
            <h2 className='text-xl font-semibold mt-4'>
              {lineConnected ? t('account.lineConnection.connected') : t('account.lineConnection.notConnected')}
            </h2>

            <p className='text-gray-600 text-center mt-2 max-w-md'>
              {lineConnected
                ? t('account.lineConnection.connectedDescription')
                : t('account.lineConnection.notConnectedDescription')}
            </p>

            <div className='mt-6'>
              {lineConnected ? (
                <button
                  onClick={handleDisconnectLine}
                  disabled={loading}
                  className='px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg transition-colors'
                >
                  {loading ? t('account.lineConnection.loading') : t('account.lineConnection.disconnectButton')}
                </button>
              ) : (
                <button
                  onClick={handleConnectLine}
                  disabled={loading}
                  className='px-6 py-2 bg-[#00B900] hover:bg-[#00A000] text-white rounded-lg flex items-center justify-center transition-colors'
                >
                  {loading ? t('account.lineConnection.loading') : t('account.lineConnection.connectButton')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  )
}
